import OpenInNew from "@mui/icons-material/OpenInNew";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { UseSupabaseLoaderState } from "../../../../../server/supabase/hooks/types";
import type { ListenEntitlement } from "../../../../generic/server/supabase-account-service";
import type { MomentFormSurvey } from "../../../../survey/server/supabase-survey-service";

const InfoAlert: FC<{
  children: ReactNode;
  handleClick?: () => void;
}> = ({ children, handleClick }) => (
  <Alert
    severity="info"
    sx={{
      my: 4,
    }}
    slotProps={{
      message: {
        sx: {
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          textAlign: "left",
          gap: 2,
        },
      },
    }}
  >
    <Typography sx={{ flexGrow: 1 }} variant="body2">
      {children}
    </Typography>
    <Button
      endIcon={<OpenInNew />}
      data-analytics-id="moment-form-insert-button-learn-more"
      href={"/listen"}
      target="_blank"
      rel="noopener"
      onClick={handleClick}
    >
      Find&nbsp;out&nbsp;more
    </Button>
  </Alert>
);

// If trying to display a survey, it checks if there aren't any and renders an info
// message instead of the selectors.
export const ListenStatusWrapper: FC<{
  children: React.ReactNode;
  listenEntitlement: ListenEntitlement | null;
  showListenIntent: boolean;
  surveyLoader: UseSupabaseLoaderState<MomentFormSurvey[]>;
  handleClickLearnMore: () => void;
}> = ({ children, listenEntitlement, showListenIntent, surveyLoader, handleClickLearnMore }) => {
  const { t } = useTranslation();
  const noSurveysYet = !surveyLoader.data?.length;
  // If we open directly onto this, the intent is yes but the data is null
  if (showListenIntent && listenEntitlement === null) {
    return (
      <Box
        sx={{
          width: 552,
          height: 72,
          my: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress
          sx={{
            my: 4,
            mx: "auto",
          }}
        />
      </Box>
    );
  }

  if (showListenIntent && noSurveysYet && listenEntitlement === "Basic") {
    return (
      <InfoAlert handleClick={handleClickLearnMore}>
        {t("Launch an eNPS Survey to collect valuable employee feedback.")}
      </InfoAlert>
    );
  }

  if (showListenIntent && noSurveysYet && listenEntitlement === "Pro") {
    return (
      <InfoAlert handleClick={handleClickLearnMore}>
        {t("Capture employee feedback with Listen.")}
      </InfoAlert>
    );
  }

  return <>{children}</>;
};
